/* FrequentlyBoughtTogether - Related product cards styled like the homepage product grid. */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Package } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { getProductImageUrl } from '@/lib/image-url';
import { calculatePriceInclBTW } from '@/lib/btw-calculator';

interface RelatedProduct {
  id: string;
  name: string;
  base_slug: string;
  price: number;
  image: string | null;
}

interface FrequentlyBoughtTogetherProps {
  currentProductId: string;
  subcategoryId: string | null;
  categorySlug: string;
}

export default function FrequentlyBoughtTogether({
  currentProductId,
  subcategoryId,
  categorySlug,
}: FrequentlyBoughtTogetherProps) {
  const [products, setProducts] = useState<RelatedProduct[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRelated = async () => {
      if (!subcategoryId) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('products')
          .select('id, name, base_slug, btw_percentage')
          .eq('subcategory_id', subcategoryId)
          .eq('active', true)
          .neq('id', currentProductId)
          .limit(4);

        if (error) throw error;
        if (!data || data.length === 0) {
          setLoading(false);
          return;
        }

        const results: RelatedProduct[] = await Promise.all(
          data.map(async (p: any) => {
            const { data: variant } = await supabase
              .from('product_variants')
              .select('price_tier_1')
              .eq('product_id', p.id)
              .eq('active', true)
              .order('price_tier_1', { ascending: true })
              .limit(1)
              .maybeSingle();

            const { data: img } = await supabase
              .from('product_images')
              .select('storage_path, uses_new_bucket')
              .eq('product_id', p.id)
              .order('display_order')
              .limit(1)
              .maybeSingle();

            const priceExcl = parseFloat(variant?.price_tier_1 || '0');
            const btw = p.btw_percentage || 9;
            const priceIncl = priceExcl > 0 ? calculatePriceInclBTW(priceExcl, btw) : 0;

            return {
              id: p.id,
              name: p.name,
              base_slug: p.base_slug,
              price: priceIncl,
              image: img ? getProductImageUrl(img.storage_path, img.uses_new_bucket) : null,
            };
          })
        );

        setProducts(results.filter((r) => r.price > 0));
      } catch (error) {
        console.error('Error fetching related products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRelated();
  }, [currentProductId, subcategoryId]);

  if (loading || products.length === 0) return null;

  return (
    <section className="mb-16">

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {products.map((product, index) => (
          <motion.div
            key={product.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.08 }}
            viewport={{ once: true }}
          >
            <Link to={`/${product.base_slug}`}>
              <div
                className="group relative flex h-full flex-col overflow-hidden rounded-xl border border-transparent bg-white transition-all duration-200 hover:ring-2 hover:ring-[#1e2b22]"
                style={{ ['--tw-ring-offset-color' as string]: '#fcfaf6', ['--tw-ring-offset-width' as string]: '3px' }}
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-[#e7e1d6]">
                  {product.image ? (
                    <img
                      src={product.image}
                      alt={product.name}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-[#e7e1d6]">
                      <Package className="h-16 w-16 text-[#cfcac2]" />
                    </div>
                  )}
                  <div className="absolute right-2.5 top-2.5 z-10 rounded-[5px] bg-[#1e2b22] px-2 py-1 text-[10px] font-bold text-white sm:right-3 sm:top-3 sm:px-2.5 sm:py-1.5 sm:text-xs">
                    Vanaf €{product.price.toFixed(2)}
                  </div>
                </div>
                <div className="flex flex-1 items-center justify-between gap-1 border-t border-gray-100 bg-white px-3 py-2.5">
                  <span className="line-clamp-2 break-words text-xs font-medium leading-snug text-[#1e2b22] sm:text-[13px]">
                    {product.name}
                  </span>
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#1e2b22] transition-colors group-hover:bg-[#5a6552]" aria-hidden="true">
                    <ArrowRight className="h-4 w-4 text-white transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
